import { Edit as EditIcon } from '@mui/icons-material';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Container, Typography, useTheme } from '@mui/material';
import React, { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useBranding } from '../../../contexts/BrandingContext';
import { useAuth } from '../../../contexts/AuthContext';
import { parseApiError } from '../../../library/apiErrorHelper';
import { localised } from '../../../library/models/localisedText';
import { platformAboutProfile } from '../../../library/models/platformAbout';
import { ITenantProfile, ITenantProfileCard, ProfileDraft, invalidCardKeys, toProfileDraft, toTenantProfileRequest } from '../../../library/models/tenantProfile';
import ResourcesIds from '../../../resources/ResourcesIds';
import { useReactMutation, useReactQuery } from '../../../services/queryService';
import { services } from '../../../services/services';
import { ConfirmationDialog } from '../../confirmationDialog/confirmationDialog';
import { useSnackbar } from '../../layout/snackbarProvider';
import { AboutEditor } from './AboutEditor';
import { aboutWrapperStyles, alertStyles, blockedSaveHintStyles, callToActionStyles, editingActionsStyles, emptyStateBoxStyles, emptyStateSubtitleStyles, eyebrowStyles, featureAccentBarStyles, featureCardStyles, featureGridStyles, headerActionsStyles, iconTileStyles, loadingBoxStyles, sectionTitleStyles, serviceCardStyles, serviceGridStyles, subtitleTextStyles, titleContainerStyles, titleTextStyles } from './about.styles';
import { profileIcon } from './profileIcons';

/**
 * What the clinic says about itself, and -- for the clinic's own administrator -- the
 * place it is written.
 *
 * The copy is per clinic and comes from the API, which takes the clinic from the session;
 * this page never names one. Edit mode is the same page rather than a separate console
 * screen, so an admin rewrites the mission while looking at the page their customers see.
 * It is hidden from everyone else, and the API refuses the save regardless.
 *
 * Every block draws only if the clinic wrote it. A clinic with no mission gets no
 * "Our Mission" heading over nothing, which is what lets a half-written page still read
 * properly -- and what makes the first save a matter of filling in the parts you want
 * rather than all of them.
 */
export const About = (): JSX.Element => {
    const theme = useTheme();
    const { i18n, t: translate } = useTranslation();
    const { isAuthenticated, role } = useAuth();
    const { hostContext, hostname, isHostPending, isPlatformHost } = useBranding();
    const { showSnackbar } = useSnackbar();

    /**
     * Signed in, this is the clinic's own copy. Signed out, it is the same copy
     * read through the public front-door route.
     *
     * This page has always been a PUBLIC route fetching an AUTHENTICATED endpoint, so a
     * visitor with no session got a 401 and a blank page. That was survivable while the only
     * address was the platform's; it is not now that a clinic's own domain is meant to be
     * their whole public presence.
     *
     * The public route names no clinic -- it resolves one from the hostname -- so there is
     * nothing to fetch when the hostname names none, which is what `enabled` says.
     */
    const { data, isLoading, refetch } = useReactQuery<ITenantProfile>(
        isAuthenticated
            ? services.tenantProfile.getTenantProfile()
            : services.publicTenant.getTenantProfile(hostname),
        { enabled: isAuthenticated || hostContext !== null }
    );

    /**
     * A signed-out visitor on the platform's own address, who gets Carefull's page rather than
     * a clinic's -- see {@link platformAboutProfile}. Signed in, the platform's address still
     * shows the visitor's own clinic, exactly as before.
     *
     * `isPlatformHost` rather than `hostContext === null`: the latter is also true on a
     * clinic's domain while its tenant-context request is in flight, and drawing Carefull's
     * page there for that long is somebody else's front door on the clinic's address.
     */
    const isPlatformVisitor: boolean = !isAuthenticated && isPlatformHost;

    /** Signed out, and the hostname has not yet said whose address this is. */
    const isWaitingForHost: boolean = !isAuthenticated && isHostPending;

    const profile: ITenantProfile | undefined = isPlatformVisitor
        ? platformAboutProfile(translate)
        : data;

    const saveProfile = useReactMutation(services.tenantProfile.saveTenantProfileMutation());

    /** The working copy while editing, or null when the page is read-only. */
    const [draft, setDraft] = React.useState<ProfileDraft | null>(null);
    const [isDiscardOpen, setIsDiscardOpen] = React.useState(false);

    /**
     * Whether the profile was actually fetched, which is not the same as "isError is
     * false". A query can also settle without data and without an error -- React Query
     * pauses one it believes cannot reach the network -- and treating that as an empty
     * result would tell a visitor the clinic has nothing to say when nothing was loaded.
     */
    const hasLoaded: boolean = profile !== undefined;
    const isEditing: boolean = draft !== null;
    const canEdit: boolean = role === 'ADMIN';

    const invalidKeys: Set<string> = React.useMemo(
        () => (draft ? invalidCardKeys(draft) : new Set<string>()),
        [draft]
    );

    const text = (ro: string | null | undefined, en: string | null | undefined): string =>
        localised(ro ?? '', en, i18n.language);

    const startEditing = (): void => setDraft(toProfileDraft(data));

    const cancelEditing = (): void => {
        setDraft(null);
        setIsDiscardOpen(false);
    };

    /**
     * Asks before discarding, because there is no autosave and a mistyped click here
     * loses everything typed since Edit. An untouched draft closes straight away --
     * confirming a decision the admin has not made yet is just noise.
     */
    const requestCancel = (): void => {
        const isUntouched: boolean =
            draft !== null
            && JSON.stringify(toTenantProfileRequest(draft))
            === JSON.stringify(toTenantProfileRequest(toProfileDraft(data)));

        if (isUntouched) {
            cancelEditing();
            return;
        }

        setIsDiscardOpen(true);
    };

    const save = (): void => {
        if (!draft) {
            return;
        }

        saveProfile.mutate(toTenantProfileRequest(draft), {
            onSuccess: () => {
                showSnackbar(translate(ResourcesIds.ABOUT_SAVE_SUCCESS), 'success');
                setDraft(null);
                refetch();
            },
            // The draft stays on screen: a failed save is the one moment the admin most
            // needs what they typed.
            onError: (error: unknown) => {
                const parsed = parseApiError(error);
                showSnackbar(parsed.message ?? translate(ResourcesIds.ABOUT_SAVE_ERROR), 'error');
            },
        });
    };

    const features: ITenantProfileCard[] = profile?.features ?? [];
    const serviceCards: ITenantProfileCard[] = profile?.services ?? [];

    const aboutTitle: string = text(profile?.aboutTitleRo, profile?.aboutTitleEn);
    const aboutSubtitle: string = text(profile?.aboutSubtitleRo, profile?.aboutSubtitleEn);
    const missionTitle: string = text(profile?.missionTitleRo, profile?.missionTitleEn);
    const missionBody: string = text(profile?.missionBodyRo, profile?.missionBodyEn);
    const featuresTitle: string = text(profile?.featuresTitleRo, profile?.featuresTitleEn);
    const servicesTitle: string = text(profile?.servicesTitleRo, profile?.servicesTitleEn);
    const ctaTitle: string = text(profile?.ctaTitleRo, profile?.ctaTitleEn);
    const ctaSubtitle: string = text(profile?.ctaSubtitleRo, profile?.ctaSubtitleEn);
    const ctaContact: string = text(profile?.ctaContactRo, profile?.ctaContactEn);

    /** A clinic that has written nothing at all, as opposed to one missing a block. */
    const isBlank: boolean = [aboutTitle, aboutSubtitle, missionTitle, missionBody,
        ctaTitle, ctaSubtitle, ctaContact].every(value => value.length === 0)
        && features.length === 0
        && serviceCards.length === 0;

    const renderProfile = (): JSX.Element => (
        <>
            {(aboutTitle.length > 0 || aboutSubtitle.length > 0) && (
                <Box sx={titleContainerStyles}>
                    <Box sx={eyebrowStyles}>{translate(ResourcesIds.ABOUT)}</Box>

                    {aboutTitle.length > 0 && (
                        <Typography variant="h2" component="h1" gutterBottom sx={titleTextStyles(theme)}>
                            {aboutTitle}
                        </Typography>
                    )}

                    {aboutSubtitle.length > 0 && (
                        <Typography variant="h5" color="text.secondary" sx={subtitleTextStyles}>
                            {aboutSubtitle}
                        </Typography>
                    )}
                </Box>
            )}

            {(missionTitle.length > 0 || missionBody.length > 0) && (
                <Box sx={{ mb: 8 }}>
                    {missionTitle.length > 0 && (
                        <Typography variant="h4" component="h2" gutterBottom sx={sectionTitleStyles}>
                            {missionTitle}
                        </Typography>
                    )}

                    {missionBody.length > 0 && (
                        <Typography variant="h6" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
                            {missionBody}
                        </Typography>
                    )}
                </Box>
            )}

            {features.length > 0 && (
                <Box sx={{ mb: 8 }}>
                    {featuresTitle.length > 0 && (
                        <Typography variant="h4" component="h2" gutterBottom sx={sectionTitleStyles}>
                            {featuresTitle}
                        </Typography>
                    )}

                    <Box sx={featureGridStyles}>
                        {features.map((feature) => (
                            <Card key={feature.id} sx={featureCardStyles}>
                                <Box sx={featureAccentBarStyles} />
                                <CardContent>
                                    <Box sx={iconTileStyles}>{profileIcon(feature.icon, theme)}</Box>
                                    <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                                        {text(feature.titleRo, feature.titleEn)}
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary">
                                        {text(feature.bodyRo, feature.bodyEn)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                </Box>
            )}

            {serviceCards.length > 0 && (
                <Box sx={{ mb: 8 }}>
                    {servicesTitle.length > 0 && (
                        <Typography variant="h4" component="h2" gutterBottom sx={sectionTitleStyles}>
                            {servicesTitle}
                        </Typography>
                    )}

                    <Box sx={serviceGridStyles}>
                        {serviceCards.map((service) => (
                            <Card key={service.id} sx={serviceCardStyles}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                    {text(service.titleRo, service.titleEn)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {text(service.bodyRo, service.bodyEn)}
                                </Typography>
                            </Card>
                        ))}
                    </Box>
                </Box>
            )}

            {(ctaTitle.length > 0 || ctaSubtitle.length > 0 || ctaContact.length > 0) && (
                <Box sx={callToActionStyles(theme)}>
                    {ctaTitle.length > 0 && (
                        <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
                            {ctaTitle}
                        </Typography>
                    )}

                    {ctaSubtitle.length > 0 && (
                        <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
                            {ctaSubtitle}
                        </Typography>
                    )}

                    {ctaContact.length > 0 && (
                        <Typography variant="body1" color="text.secondary">
                            {ctaContact}
                        </Typography>
                    )}
                </Box>
            )}
        </>
    );

    const renderEmptyState = (): JSX.Element => (
        <Box sx={emptyStateBoxStyles}>
            <Typography variant="h6" gutterBottom>
                {translate(ResourcesIds.ABOUT_EMPTY_TITLE)}
            </Typography>
            <Typography variant="body2" sx={emptyStateSubtitleStyles}>
                {translate(canEdit
                    ? ResourcesIds.ABOUT_EMPTY_ADMIN_SUBTITLE
                    : ResourcesIds.ABOUT_EMPTY_SUBTITLE)}
            </Typography>
        </Box>
    );

    return (
        <Container maxWidth="lg">
            <Box sx={aboutWrapperStyles}>
                {canEdit && hasLoaded && (
                    <Box sx={isEditing ? editingActionsStyles : headerActionsStyles}>
                        {isEditing ? (
                            <>
                                <Button
                                    variant="contained"
                                    onClick={save}
                                    disabled={invalidKeys.size > 0 || saveProfile.isPending}
                                >
                                    {translate(ResourcesIds.ABOUT_SAVE_ACTION)}
                                </Button>

                                <Button variant="outlined" onClick={requestCancel}>
                                    {translate(ResourcesIds.CANCEL_TEXT)}
                                </Button>

                                {/* Travels with the button, so a disabled Save is never a
                                    mystery from a part of the page that looks fine. */}
                                {invalidKeys.size > 0 && (
                                    <Typography variant="body2" sx={blockedSaveHintStyles}>
                                        {/* `fields`, not `count`: i18next treats `count` as the
                                            plural selector, and Romanian's three plural forms
                                            are not worth carrying for a number badge. */}
                                        {translate(ResourcesIds.ABOUT_BLOCKED_SAVE, { fields: invalidKeys.size })}
                                    </Typography>
                                )}
                            </>
                        ) : (
                            <Button variant="contained" startIcon={<EditIcon />} onClick={startEditing}>
                                {translate(ResourcesIds.ABOUT_EDIT_ACTION)}
                            </Button>
                        )}
                    </Box>
                )}

                {!isLoading && !isWaitingForHost && !hasLoaded && (
                    <Alert severity="error" sx={alertStyles}>
                        {translate(ResourcesIds.ABOUT_LOAD_ERROR)}
                    </Alert>
                )}

                {isLoading || isWaitingForHost ? (
                    <Box sx={loadingBoxStyles}>
                        <CircularProgress size={60} />
                    </Box>
                ) : !hasLoaded ? null : draft !== null ? (
                    <AboutEditor
                        draft={draft}
                        invalidKeys={invalidKeys}
                        onChange={setDraft}
                    />
                ) : isBlank ? (
                    renderEmptyState()
                ) : (
                    renderProfile()
                )}

                <ConfirmationDialog
                    isOpen={isDiscardOpen}
                    onClose={() => setIsDiscardOpen(false)}
                    onConfirm={cancelEditing}
                    title={translate(ResourcesIds.ABOUT_DISCARD_TITLE)}
                    description={translate(ResourcesIds.ABOUT_DISCARD_DESCRIPTION)}
                    confirmMessage={translate(ResourcesIds.ABOUT_DISCARD_CONFIRM)}
                    denyMessage={translate(ResourcesIds.CANCEL_TEXT)}
                />
            </Box>
        </Container>
    );
};
